import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import cn from "classnames";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import getName from "../../../../common/helpers/getName";
import { useAfterHydration } from "../../../../common/helpers/afterHydration";
import { checkPlayable } from "../../../../common/parameters";

import { IStateTree } from "../../../store/interfaces";
import routing from "../../../routing";
import socket from "../../../socket";
import Icons from "../../../icons";
import Icon from "../../../icon";

import Scores from "./Scores";
import Timer from "./Timer";

import {
    UpperMenu,
    MenuRight,
    MenuLeft,
    Progress,
    ArrowButton,
    Logo,
    JoinBanner,
    Students,
    StudentsDropdown,
    Student,
    StudentsSummary,
    Button,
    MenuOpener,
    MenuDropdown,
} from "./styles";
import messages from "./messages";
import { IProps } from "./interfaces";
import { IUser } from "../../../store/user/models";

const Menu = ({
    taskIndex,
    plan,
    join,
    students,
    results,
    startedAt,
    closeClassroom,
    emptyHistory,
    restart,
    showSolution,
    switchIsLow,
    isSimulation,
    isExperience,
    goToPage,
}: IProps) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const user = useSelector((state: IStateTree) => state.user) as IUser;
    // The classroom address is read off window.location, which the server cannot do, so
    // the banner shows the set's name until hydration is over and both sides agree.
    const afterHydration = useAfterHydration();
    const [studentsOpen, setStudentsOpen] = useState(false);
    const [showScores, setShowScore] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const go = (way: number) => {
        let _taskIndex: number;
        if (way === -1) {
            _taskIndex = Math.max(taskIndex! - 1, 0);
        } else {
            _taskIndex = Math.min(taskIndex! + 1, plan.tasks.length - 1);
        }
        goToPage(_taskIndex);
        if (socket.com) {
            socket.com.emit("go-to", { taskIndex: _taskIndex });
        }
    };
    const clickOutside = () => {
        setIsMenuOpen(false);
        window.removeEventListener("click", clickOutside);
    };
    useEffect(() => {
        return () => {
            window.removeEventListener("click", clickOutside);
        }
    }, []);
    const handleOpenMobileMenu = () => {
        if (!isMenuOpen) {
            setTimeout(() => {
                window.addEventListener("click", clickOutside);
            });
        }
        setIsMenuOpen(old => !old)
    };
    const handleShowScores = () => {
        setShowScore(true);
    };
    const handleHideScores = () => {
        setShowScore(false);
    };
    const task = taskIndex !== null && plan.tasks[taskIndex];
    const embedTask = task && (task.type === "embed" || task.type === "subScore");
    const isPlayable = task && checkPlayable(task, plan);
    return (
        <UpperMenu>
            {showScores && <Scores plan={plan} close={handleHideScores} />}
            <MenuLeft>
                {emptyHistory && !user.loggedIn ? (
                    <Logo to="/">
                        <Icons.Logo />
                    </Logo>
                ) : (
                    <ArrowButton
                        onClick={() => {
                            if (window.historyList.length === 0) {
                                navigate("/");
                            } else {
                                navigate(-1);
                            }
                        }}
                        style={{
                            paddingRight: 6,
                        }}
                    >
                        <Icon i="arrowback" />
                    </ArrowButton>
                )}
                {!isSimulation && (
                    <MenuOpener onClick={handleOpenMobileMenu}>
                        <div className={cn(isMenuOpen && "Active")}>
                            <div className={cn("Line", "Line1")}></div>
                            <div className={cn("Line", "Line2")}></div>
                            <div className={cn("Line", "Line3")}></div>
                        </div>
                    </MenuOpener>
                )}
                {!isSimulation && isMenuOpen && (
                    <MenuDropdown>
                        {!(embedTask || !isPlayable) && (
                            <Button
                                onClick={restart}
                            >
                                <Icon i="restart-homework" />
                                <FormattedMessage {...messages.restartTask} />
                            </Button>
                        )}
                        {!(embedTask || !isPlayable) && (
                            <Button
                                onClick={showSolution}
                            >
                                <Icon i="show-answer" />
                                <FormattedMessage {...messages.showSolution} />
                            </Button>
                        )}
                        {!isExperience && !(embedTask || !isPlayable) && (
                            <Button
                                onClick={switchIsLow}
                            >
                                <Icon i="toggle-appearance" />
                                <FormattedMessage {...messages.fancyUI} />
                            </Button>
                        )}
                        {(plan.scores?.length || 0) > 0 && (
                            <Button
                                onClick={handleShowScores}
                            >
                                <Icon i="scores" />
                                <FormattedMessage {...messages.showScores} />
                            </Button>
                        )}
                        {startedAt && (
                            <Button onClick={closeClassroom}>
                                <Icon i="close-classroom" />
                                <FormattedMessage {...messages.closeRoom} />
                            </Button>
                        )}
                    </MenuDropdown>
                )}
                {startedAt && <Timer startedAt={startedAt} />}
            </MenuLeft>

            {join && !isSimulation && afterHydration ? (
                <JoinBanner>
                    {window.location.host + "/" + routing.join}<span className="class-key">#{join?.key}</span>
                </JoinBanner>
            ) : (
                <JoinBanner>{getName(plan.name, intl)}</JoinBanner>
            )}
            <MenuRight>
                {students && students.length > 0 && (
                    <Students
                        onMouseEnter={() => setStudentsOpen(true)}
                        onMouseLeave={() => setStudentsOpen(false)}
                    >
                        <StudentsSummary>
                            <FormattedMessage {...messages.solved} />:{" "}
                            {results![taskIndex!] ? results![taskIndex!].length : 0}/{students.length}
                        </StudentsSummary>
                        {studentsOpen && (
                            <StudentsDropdown>
                                {students.map((student, key) => {
                                    let done = false;
                                    if (results![taskIndex!]) {
                                        done =
                                            results![taskIndex!].findIndex(
                                                result => result.studentId === student.studentId
                                            ) !== -1;
                                    }
                                    return (
                                        <Student key={key}>
                                            {student.name} {done && " " && <Icon i="check" />}
                                        </Student>
                                    );
                                })}
                            </StudentsDropdown>
                        )}
                    </Students>
                )}
                {!isExperience && (
                    <>
                        <ArrowButton className={cn(taskIndex === 0 && "Inactive")} onClick={() => go(-1)}>
                            <Icon i="arrowback" />
                        </ArrowButton>
                        <Progress>
                            {taskIndex! + 1} / {plan.tasks.length}
                        </Progress>
                        <ArrowButton
                            className={cn(taskIndex === plan.tasks.length - 1 && "Inactive", "Flip")}
                            onClick={() => go(1)}
                        >
                            <Icon i="arrowback" />
                        </ArrowButton>
                    </>
                )}
            </MenuRight>
        </UpperMenu>
    );
}

export default Menu;
